import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Zap, Snowflake } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Curated fallback shown until the owner uploads their own photos.
const FALLBACK = [
  { image_url: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Bedroom AC Install", tag: "Air Con" },
  { image_url: "https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", title: "Consumer Unit Upgrade", tag: "Electrical" },
  { image_url: "https://images.unsplash.com/photo-1531749086006-778f081e9fd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "LED Downlighting", tag: "Electrical" },
  { image_url: "https://images.unsplash.com/photo-1593941707874-ef25b8b4a92b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Home EV Charger", tag: "Electrical" },
  { image_url: "https://images.unsplash.com/photo-1757219525975-03b5984bc6e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", title: "Living Room Split Unit", tag: "Air Con" },
  { image_url: "https://images.pexels.com/photos/28950842/pexels-photo-28950842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", title: "Full Rewire — Fuse Board", tag: "Electrical" },
  { image_url: "https://images.pexels.com/photos/13061307/pexels-photo-13061307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", title: "External Condenser Units", tag: "Air Con" },
  { image_url: "https://images.pexels.com/photos/10432229/pexels-photo-10432229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200", title: "Feature Ceiling Light", tag: "Electrical" },
];

const resolveUrl = (u) => (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export const Gallery = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start", dragFree: true });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [projects, setProjects] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          // Show owner's photos; top up with curated items until the carousel feels full.
          setProjects(data.length >= 4 ? data : [...data, ...FALLBACK].slice(0, 6));
        }
      })
      .catch(() => {});
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, onSelect, projects]);

  return (
    <section id="work" className="py-24 lg:py-32 bg-white border-y border-black/10 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-brand-blue mb-4">Recent work</p>
            <h2 className="font-display font-extrabold tracking-tighter text-ink text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
              Jobs we're proud of.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => emblaApi?.scrollPrev()} disabled={!canPrev} data-testid="gallery-prev-btn" aria-label="Previous"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-ink hover:bg-ink hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink transition-colors duration-200">
              <ArrowLeft size={18} />
            </button>
            <button onClick={() => emblaApi?.scrollNext()} disabled={!canNext} data-testid="gallery-next-btn" aria-label="Next"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-ink hover:bg-ink hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink transition-colors duration-200">
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] pl-6 lg:pl-10">
        <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef} data-testid="gallery-carousel">
          <div className="flex gap-5 pr-6 lg:pr-10">
            {projects.map((p, i) => (
              <motion.div key={p.id || i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                data-testid={`gallery-slide-${i}`}
                className="relative shrink-0 w-[80%] sm:w-[48%] lg:w-[32%] aspect-[4/5] rounded-2xl overflow-hidden border border-black/10 group">
                <img src={resolveUrl(p.image_url)} alt={p.title || "JD Electrical work"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" draggable="false" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
                <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-body text-xs font-semibold ${
                  p.tag === "Air Con" ? "bg-brand-blue text-white" : "bg-brand-yellow text-ink"
                }`}>
                  {p.tag === "Air Con" ? <Snowflake size={12} /> : <Zap size={12} />} {p.tag}
                </span>
                {p.title && <h3 className="absolute bottom-5 left-5 right-5 font-display text-2xl font-bold tracking-tight text-white">{p.title}</h3>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
